import React, { useMemo } from 'react';
import { StyleSheet, Animated, Platform, View, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { openCreditsRecoveryModal } from '@/constants/utils/trial';
import { useModalStore } from '@/store/modal';
import { useToastStore } from '@/store/toast';
import { useMemoryStore } from '@/store/memory';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import { RESPONSE_ACTION_TYPES, BORDER_RADIUS, MESSAGE_REGENERATE_TYPES } from '@/constants/config';
import { authApi } from '@/lib/api';
import { PLUS_COLORS, isDarkTheme } from '@/constants/Colors';
import socketManager from '@/lib/socket';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const TOAST_TYPES = ['success', 'error', 'earn', 'default', 'info', 'warning', 'plus'];
const WARNING_TONE = '#D97706';
const INFO_TONE = '#3B82F6';

function withAlpha(color, alpha) {
  if (!color || typeof color !== 'string') return `rgba(0, 0, 0, ${alpha})`;
  const rgb = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  const hex = color.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (full.length !== 6) return color;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return color;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toastTone(type, colors, hasPlusAction) {
  if (hasPlusAction || type === 'plus') return PLUS_COLORS.textColorPurple;
  if (type === 'success' || type === 'earn') return colors.color_success;
  if (type === 'error') return colors.color_destructive;
  if (type === 'warning') return WARNING_TONE;
  if (type === 'info') return INFO_TONE;
  return colors.text_color_secondary;
}

function toastIconName(type) {
  switch (type) {
    case 'success':
      return 'circle-check';
    case 'earn':
      return 'gift';
    case 'error':
      return 'circle-xmark';
    case 'plus':
      return 'sparkles';
    case 'warning':
      return 'triangle-exclamation';
    default:
      return 'circle-info';
  }
}

const createStyles = (colors, isXS, topInset = 0, isDark = false) => StyleSheet.create({
  container: {
    position: 'absolute',
    ...Platform.select({
      web: {
        ...(isXS ? {
          top: 65,
          left: 0,
          right: 0,
          alignItems: 'center',
        } : {
          bottom: 24,
          right: 24,
        }),
      },
      ios: {
        top: topInset + 53,
        left: 0,
        right: 0,
        alignItems: 'center',
      },
      android: {
        top: topInset + 53,
        left: 0,
        right: 0,
        alignItems: 'center',
      },
      default: {
        top: 60,
        left: 0,
        right: 0,
        alignItems: 'center',
      }
    }),
    zIndex: 9999,
  },
  toastWrapper: {
    marginBottom: 10,
    ...Platform.select({
      web: {
        minWidth: isXS ? '90%' : 280,
        maxWidth: isXS ? 420 : 360,
      },
      default: {
        width: '90%',
        maxWidth: 360,
      }
    }),
  },
  toast: {
    paddingVertical: 12,
    paddingLeft: 12,
    paddingRight: 8,
    borderRadius: BORDER_RADIUS.medium,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    ...Platform.select({
      web: {
        boxShadow: isDark
          ? '0 8px 28px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04)'
          : '0 10px 32px rgba(15, 23, 42, 0.10), 0 2px 6px rgba(15, 23, 42, 0.04)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      },
      ios: {
        marginHorizontal: 8,
        marginTop: 6,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: isDark ? 0.35 : 0.12,
        shadowRadius: 20,
      },
      android: {
        marginHorizontal: 10,
        marginTop: 10,
        elevation: 6,
      },
      default: {
        marginHorizontal: 16,
        marginTop: 16,
      }
    }),
  },
  toastWithActions: {
    alignItems: 'flex-start',
  },
  contentContainer: {
    flexDirection: 'column',
    flex: 1,
    alignItems: 'flex-start',
    minWidth: 0,
    paddingTop: 1,
  },
  iconWell: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  message: {
    flexShrink: 1,
    lineHeight: 20,
  },
  actionsContainer: {
    flexDirection: 'row',
    marginTop: 10,
    gap: 6,
    flexWrap: 'wrap',
  },
  actionButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: BORDER_RADIUS.small + 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.solid_color_tertiary,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  plusActionButton: {
    backgroundColor: withAlpha(PLUS_COLORS.textColorPurple, 0.12),
  },
  actionIcon: {
    marginRight: 0,
  },
  dismiss: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 4,
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});

// Memoized toast icon component
const ToastIcon = React.memo(({ type, colors, styles, hasPlusAction }) => {
  const tone = toastTone(type, colors, hasPlusAction);
  return (
    <View style={[styles.iconWell, { backgroundColor: withAlpha(tone, 0.14) }]}>
      <Icon
        name={toastIconName(type)}
        size={14}
        color={tone}
        accessibilityLabel={`${type} icon`}
      />
    </View>
  );
});

ToastIcon.propTypes = {
  type: PropTypes.oneOf(TOAST_TYPES).isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  hasPlusAction: PropTypes.bool,
};

ToastIcon.displayName = 'ToastIcon';

// Memoized toast actions component
const ToastActions = React.memo(({ actions, handleActionPress, hasPlusAction, colors, styles }) => {
  if (!actions || !Array.isArray(actions) || actions.length === 0) return null;

  return (
    <View style={styles.actionsContainer}>
      {actions.map((action, index) => (
        <Pressable
          key={index}
          onPress={() => handleActionPress(action)}
          style={[
            styles.actionButton,
            hasPlusAction && styles.plusActionButton
          ]}
          accessibilityRole="button"
          accessibilityLabel={action.title}
          accessibilityHint={action.hint || `Perform ${action?.title?.toLowerCase()} action`}
        >
          {action.icon && (
            <Icon
              name={action.icon}
              size={12}
              color={hasPlusAction ? PLUS_COLORS.textColorPurple : colors.text_color_primary}
              style={styles.actionIcon}
              accessibilityLabel={`${action.icon} icon`}
            />
          )}
          <Text
            size="xs"
            weight="medium"
            color="primary"
            style={hasPlusAction ? { color: PLUS_COLORS.textColorPurple } : undefined}
          >
            {action.title}
          </Text>
        </Pressable>
      ))}
    </View>
  );
});

ToastActions.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon: PropTypes.string,
    hint: PropTypes.string,
    href: PropTypes.string,
    action: PropTypes.string,
    data: PropTypes.object,
    keepToastOpen: PropTypes.bool,
  })),
  handleActionPress: PropTypes.func.isRequired,
  hasPlusAction: PropTypes.bool.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

ToastActions.displayName = 'ToastActions';

// Memoized toast component
const Toast = React.memo(({ toast, styles, colors, onRemove }) => {
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const enterFrom = Platform.OS === 'web' && !isXS ? 12 : -12;
  const opacity = React.useRef(new Animated.Value(0)).current;
  const translateY = React.useRef(new Animated.Value(enterFrom)).current;
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const userId = useAuthStore(state => state.user?.id);
  const isAnon = !userId;
  const requireAuth = useRequireAuth();
  
  // Filter out buttons with invalid titles and go-plus button for Plus users
  const validActions = toast?.actions && Array.isArray(toast.actions) && toast.actions.length > 0 ? toast.actions.filter(action => {
    // First check if title exists and is not empty
    if (!action?.title || action?.title.trim() === '') {
      return false;
    }

    // Check for invalid title content
    const invalidContent = ['undefined', 'null', 'function', '()'];
    const hasInvalidContent = invalidContent.some(invalid => action?.title.toLowerCase().includes(invalid));
    
    if (hasInvalidContent) {
      action.title = 'Error';
    }

    // Plus: hide go-plus. Free: if buy-credits is present (model gate), hide
    // go-plus so one unlock CTA opens ModelAccessPaywallModal.
    if (isPlus) {
      return action.action !== RESPONSE_ACTION_TYPES.GO_PLUS;
    }
    const hasBuyCredits = toast.actions.some((a) => a?.action === RESPONSE_ACTION_TYPES.BUY_CREDITS);
    if (action.action === RESPONSE_ACTION_TYPES.GO_PLUS && hasBuyCredits) {
      return false;
    }
    return true;
  }) : [];

  // if actions contain "go-plus", dont show red, show accent color and sparkle icon!
  const hasPlusAction = validActions.some(action => action.action === 'go-plus');

  // Handle fade + slide in on mount
  React.useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 220,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: 0,
        tension: 65,
        friction: 11,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const handlePress = React.useCallback(() => {
    // Fade + slide out then remove
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: enterFrom,
        duration: 180,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onRemove(toast.id);
    });
  }, [onRemove, toast.id, opacity, translateY, enterFrom]);

  const handleActionPress = React.useCallback(async (action) => {
    try {
      // Handle direct navigation
      if (action.href) {
        router.push(action.href);
        if (!action.keepToastOpen) {
          handlePress();
        }
        return;
      }

      // Handle special actions
      switch (action.action) {
        case RESPONSE_ACTION_TYPES.MAKE_NEW_CONVERSATION:
          router.push('/app');
          break;

        case RESPONSE_ACTION_TYPES.MAKE_NEW_CUSTOM_AGENT:
          requireAuth(() => openModal('CustomAgentModal'), {
            source: 'toast_make_new_custom_agent',
            title: 'Sign in to create a custom agent',
            subtitle: 'Custom agents are tied to your account so you can edit, share, and use them in chat.',
            icon: 'sparkles',
          });
          break;

        case RESPONSE_ACTION_TYPES.GHOST_SIGNUP:
          if (!isGhost) break;
          openModal('GhostSignupModal', {
            title: action?.data?.title,
            subtitle: action?.data?.subtitle,
          });
          break;

        case RESPONSE_ACTION_TYPES.LOGIN:
          if (Platform.OS === 'web' && !isXS) {
            openModal('LoginModal');
          } else {
            router.push('/login');
          }
          break;

        case RESPONSE_ACTION_TYPES.VIEW_FILE:
          openModal('FileEditModal', {
            fileId: action?.data?.fileId,
          });
          break;

        case RESPONSE_ACTION_TYPES.GO_PLUS:
          if (isPlus) break;
          // Pricing / upsell flows still work for free logged-in users
          // (the whole point) — but for anon visitors there's no
          // account to subscribe with yet, so we surface the auth CTA
          // first and they can revisit the pricing modal post-signup.
          requireAuth(
            () => openModal('PricingModal', {
              title: action?.data?.title,
              subtitle: action?.data?.subtitle,
            }),
            {
              source: 'toast_go_plus',
              title: 'Sign in to upgrade to Plus',
              subtitle: 'Plus is tied to your account so we can apply your subscription.',
              icon: 'crown',
            },
          );
          break;

        case RESPONSE_ACTION_TYPES.BUY_CREDITS:
          // Free users: model-gate paywall (buy credits OR Plus). Plus: every
          // way forward (Arya free / buy / Premium / refresh date).
          if (!isPlus) {
            openModal('ModelAccessPaywallModal', {
              title: action?.data?.title,
              subtitle: action?.data?.subtitle,
            });
            break;
          }
          openModal('OutOfCreditsModal', { source: 'toast' });
          break;

        case RESPONSE_ACTION_TYPES.GET_CREDITS:
          // For free users show out-of-credits, Plus users choose credit pack first.
          // Anon visitors can't have credits — route to auth CTA.
          if (isAnon) {
            requireAuth(() => {}, {
              source: 'toast_get_credits',
              title: 'Sign in to use credits',
              subtitle: 'Credits live on your account so we can apply them to image, video, and music generations.',
              icon: 'coins',
            });
            break;
          }
          if (isPlus) {
            openModal('OutOfCreditsModal', { source: 'toast' });
          } else {
            openCreditsRecoveryModal(openModal, useAuthStore.getState().user, {
              source: 'toast_get_credits',
              title: action?.data?.title,
              subtitle: action?.data?.subtitle,
            });
          }
          break;

        case RESPONSE_ACTION_TYPES.VERIFY_EMAIL:
          await authApi.resendVerificationEmail();
          addToast('Verification email sent successfully. If you don\'t see it, check your spam folder or email us at ai@gab.com.', 'success');
          break;

        case RESPONSE_ACTION_TYPES.RETRY:
          if (!socketManager.isSocketConnected()) {
            console.warn('Socket not connected. Cannot regenerate message');
            return;
          }
          socketManager.emitRegenerateMessage({
            userId,
            conversationId: action?.data?.conversationId,
            messageId: action?.data?.messageId,
            regenerateType: MESSAGE_REGENERATE_TYPES.AGAIN,
          });
          break;

        case RESPONSE_ACTION_TYPES.ADD_TO_BOOKMARK_FOLDER:
          openModal('BookmarkFolderPickerModal', {
            conversationId: action?.data?.conversationId,
            messageId: action?.data?.messageId,
          });
          break;

        case RESPONSE_ACTION_TYPES.UNDO_MEMORY: {
          // "Saved to memory" toast Undo — hard-deletes the just-saved memory.
          const memoryId = action?.data?.memoryId;
          if (memoryId) {
            useMemoryStore.getState().deleteMemory(memoryId).catch(() => {});
          }
          break;
        }

        default:
          // 
          break;
      }

      // Remove toast after action unless specified to keep open
      if (!action.keepToastOpen) {
        handlePress();
      }
    } catch (error) {
      console.error('Action failed:', error);
      addToast(error.message || 'Action failed', 'error');
    }
  }, [router, isXS, openModal, handlePress, addToast, requireAuth, isAnon, isPlus, isGhost, userId]);

  const toastType = hasPlusAction ? 'plus' : toast.type;
  const Card = validActions.length > 0 ? View : Pressable;

  return (
    <Animated.View
      style={[
        styles.toastWrapper,
        { opacity, transform: [{ translateY }] }
      ]}
      accessibilityLiveRegion="polite"
    >
      <Card
        {...(validActions.length === 0 ? { onPress: handlePress } : {})}
        style={[styles.toast, validActions.length > 0 && styles.toastWithActions]}
        accessibilityRole="alert"
        accessibilityLabel={`${toast.type} notification: ${toast.message}`}
      >
        <ToastIcon
          type={toastType}
          colors={colors}
          styles={styles}
          hasPlusAction={hasPlusAction}
        />
        <View style={styles.contentContainer}>
          <Text size="s" weight="medium" color="primary" style={styles.message}>
            {toast.message}
          </Text>
          {validActions.length > 0 && (
            <ToastActions
              actions={validActions}
              handleActionPress={handleActionPress}
              hasPlusAction={hasPlusAction}
              colors={colors}
              styles={styles}
            />
          )}
        </View>
        <Pressable
          onPress={handlePress}
          style={({ pressed, hovered }) => [
            styles.dismiss,
            (pressed || hovered) && { backgroundColor: colors.solid_color_tertiary },
          ]}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel="Dismiss notification"
        >
          <Icon name="xmark" size={11} color={colors.text_color_tertiary} />
        </Pressable>
      </Card>
    </Animated.View>
  );
});

Toast.propTypes = {
  toast: PropTypes.shape({
    id: PropTypes.number.isRequired,
    message: PropTypes.string.isRequired,
    type: PropTypes.oneOf(TOAST_TYPES).isRequired,
    actions: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      hint: PropTypes.string,
      href: PropTypes.string,
      action: PropTypes.string,
      data: PropTypes.object,
      keepToastOpen: PropTypes.bool,
    })),
  }).isRequired,
  styles: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

Toast.displayName = 'Toast';

export default function ToastRoot() {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const toasts = useToastStore(state => state.toasts);
  const { removeToast } = useToastStore(state => state.actions);
  const isDark = isDarkTheme(theme);

  const styles = useMemo(
    () => createStyles(colors, isXS, insets.top, isDark),
    [colors, isXS, insets.top, isDark],
  );

  return (
    <View style={styles.container} pointerEvents="box-none">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          styles={styles}
          colors={colors}
          onRemove={removeToast}
        />
      ))}
    </View>
  );
} 
